import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import api from '../../service/caller'; // Assuming you're using axios for API calls
import './service.css';

const PatientTrendsDashboard = () => {
  // State for selections
  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('1y'); // Default to 1 year
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // State for data from API
  const [hospitals, setHospitals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [trendAnalysis, setTrendAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState({
    data: Array(7).fill().map(() => Array(10).fill(0)),
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    hours: ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00']
  });
  useEffect(() => {
      const fetchHeatmapData = async () => {
        try {
          setIsLoading(true);
          const params = new URLSearchParams();
          if (selectedHospital) params.append('hospitalId', selectedHospital);
          
          const response = await api.get(`api/appointment-heatmap?${params.toString()}`);
          setHeatmapData(response.data);
        } catch (error) {
          toast.error("Failed to fetch appointment heatmap data");
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchHeatmapData();
    }, [selectedHospital]);

    const getCellClass = (value) => {
      if (value >= 16) return 'heatmap-cell-highest';
      if (value >= 10) return 'heatmap-cell-high';
      if (value >= 6) return 'heatmap-cell-medium';
      if (value >= 4) return 'heatmap-cell-low';
      return 'heatmap-cell-lowest';
    };

  // Periods options
  const periodOptions = [
    { value: '1m', label: 'Dernier mois' },
    { value: '3m', label: 'Dernier trimestre' },
    { value: '6m', label: '6 derniers mois' },
    { value: '1y', label: 'Année' },
  ];

  // Fetch hospitals on component mount
  useEffect(() => {
    fetchHospitals();
  }, []);

  // Fetch departments when hospital changes
  useEffect(() => {
    if (selectedHospital) {
      fetchDepartments(selectedHospital);
    }
  }, [selectedHospital]);

  // Fetch services when department changes
  useEffect(() => {
    if (selectedHospital && selectedDepartment) {
      fetchServices(selectedHospital, selectedDepartment);
    }
  }, [selectedHospital, selectedDepartment]);

  // Fetch trend data when service or period changes
  useEffect(() => {
    if (selectedHospital && selectedService && selectedPeriod) {
      fetchTrendData(selectedHospital, selectedDepartment, selectedPeriod);
    }
  }, [selectedHospital, selectedService, selectedPeriod]);

  // API calls
  const fetchHospitals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/api/hospitals/admin/` + token, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setHospitals(response.data);
      if (response.data.length > 0) {
        setSelectedHospital(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (hospitalId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/departments/hospital/${hospitalId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        }
      });
      setDepartments(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedDepartment(response.data.data[0]._id);
      } else {
        setSelectedDepartment('');
        setServices([]);
        setSelectedService('');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async (hospitalId, departmentId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/services/${hospitalId}/${departmentId}`);
      setServices(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedService(response.data.data[0]._id);
      } else {
        setSelectedService('');
        setChartData([]);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async (hospitalId, serviceId, period) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/trendsService/${hospitalId}/${serviceId}/${period}`);
      setChartData(response.data.data.chartData);
      setTrendAnalysis(response.data.data.trendAnalysis);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      window.open(`/api/dashboard/export/${selectedHospital}/${selectedService}/${format}`, '_blank');
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
    }
  };

  // Get service color and name
  const getServiceColor = () => {
    const service = services.find(s => s.id === selectedService);
    return service ? service.color : '#3B82F6';
  };

  const getServiceName = () => {
    const service = services.find(s => s.id === selectedService);
    return service ? service.name : 'Service';
  };

  // Get department name
  const getDepartmentName = () => {
    const department = departments.find(d => d.id === selectedDepartment);
    return department ? department.name : 'Département';
  };

  return (
    <div className="use-service-patient-trends-container">
      <div className='third-container'>
          <div className='cont-header'>
            <span>Heatmap of Peak Appointment Hours</span>
          </div>

          <div className="heatmap-container">
            <div className="heatmap-content">
              <div className="heatmap-main">
                <div className="heatmap-y-axis-title">Day of Week</div>

                <div className="heatmap-grid-container">
                  <div className="heatmap-y-labels">
                    {heatmapData.days.map((day) => (
                      <div key={day} className="heatmap-y-label">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="heatmap-grid">
                    {heatmapData.days.map((day, dayIndex) => (
                      <div key={day} className="heatmap-row">
                        {heatmapData.hours.map((hour, hourIndex) => (
                          <div
                            key={`${day}-${hour}`}
                            className={`heatmap-cell ${getCellClass(heatmapData.data[dayIndex][hourIndex])}`}
                          >
                            {heatmapData.data[dayIndex][hourIndex]}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="heatmap-x-labels">
                  {heatmapData.hours.map(hour => (
                    <div key={hour} className="heatmap-x-label">
                      {hour}
                    </div>
                  ))}
                </div>

                <div className="heatmap-x-axis-title">Hour of Day</div>
              </div>

              {/* <div className="heatmap-legend">
                <div className="heatmap-legend-title">Number of Appointments</div>
                {[
                  { value: 18, label: "18" },
                  { value: 16, label: "16" },
                  { value: 14, label: "14" },
                  { value: 12, label: "12" },
                  { value: 10, label: "10" },
                  { value: 8, label: "8" },
                  { value: 6, label: "6" },
                  { value: 4, label: "4" },
                  { value: 2, label: "2" }
                ].map(item => (
                  <div key={item.value} className="heatmap-legend-item">
                    <div className={`heatmap-legend-color ${getCellClass(item.value)}`}></div>
                    <span className="heatmap-legend-label">{item.label}</span>
                  </div>
                ))}
              </div> */}
              <div className="heatmap-legend">
  <div className="heatmap-legend-title">Number of Appointments</div>
  <div className="heatmap-legend-gradient">
    <div className="gradient-bar"></div>
    <div className="gradient-labels">
      {[18, 16, 14, 12, 10, 8, 6, 4, 2].map(value => (
        <div key={value} className="gradient-label">
          <div className="gradient-tick"></div>
          <span>{value}</span>
        </div>
      ))}
    </div>
  </div>
</div>
            </div>
          </div>
        </div>

      {/* {trendAnalysis && (
        <div className="use-service-patient-trends-analysis">
          <div className="use-service-patient-trends-analysis-title">Analyse Automatique</div>
          <div 
            className={`use-service-patient-trends-analysis-description ${
              trendAnalysis.isPositive ? 'positive' : 'negative'
            }`}
          >
            {trendAnalysis.message}
          </div>
        </div>
      )} */}

      {/* <div className="use-service-patient-trends-actions">
        <button 
          className="use-service-patient-trends-btn use-service-patient-trends-btn-pdf"
          onClick={() => handleExport('pdf')}
          disabled={loading || !selectedService}
        >
          PDF
        </button>
        <button 
          className="use-service-patient-trends-btn use-service-patient-trends-btn-excel"
          onClick={() => handleExport('excel')}
          disabled={loading || !selectedService}
        >
          Excel
        </button>
      </div> */}
    </div>
  );
};

export default PatientTrendsDashboard;